import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Stamp } from "lucide-react";
import { WHATSAPP_URL, HERO_IMAGE } from "@/lib/site";

const MASK_EASE = [0.76, 0, 0.24, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.85, ease: MASK_EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const mrzLines = [
  "BRA<<REGULARIZACAO<<ELEITORAL<<ASSESSORIA<<DOCUMENTAL<<<<<<<<<<<<<<<<<<",
  "CERTIDAO<CIRCUNSTANCIADA<<QUITACAO<ELEITORAL<<CONSULADO<<PASSAPORTE<<<<<",
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotateImg = useTransform(scrollYProgress, [0, 1], [0, 3]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <header ref={ref} className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div
        className="pointer-events-none absolute -top-56 -right-40 w-[620px] h-[620px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(190,143,61,0.18), rgba(190,143,61,0) 70%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-10 items-center relative">
        <motion.div style={{ y: yText }} className="relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-brass" />
            <span className="font-mono text-[11px] md:text-xs tracking-[0.22em] uppercase font-bold text-brass" data-testid="hero-eyebrow">
              Assessoria documental · Brasileiros no exterior
            </span>
          </motion.div>

          <h1 className="font-serif font-semibold text-[#16223B] text-[42px] leading-[1.04] sm:text-6xl lg:text-7xl tracking-tight mt-7">
            <MaskedLine delay={0.35}>Seu título eleitoral</MaskedLine>
            <MaskedLine delay={0.47}>resolvido antes do</MaskedLine>
            <MaskedLine delay={0.59}>
              seu passaporte <em className="italic text-brass font-medium">vencer</em>.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: MASK_EASE, delay: 0.85 }}
            className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed max-w-[520px]"
            data-testid="hero-lede"
          >
            Orientação especializada para regularizar sua situação perante a
            Justiça Eleitoral e reunir a documentação certa para o consulado —
            sem filas, sem tentativa e erro, sem risco de atraso.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: MASK_EASE, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              data-testid="hero-whatsapp-cta"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-8 py-4 font-mono text-[12.5px] font-bold uppercase tracking-[0.08em] text-paper shadow-[0_14px_30px_-12px_rgba(22,34,59,0.5)] transition-all duration-300 hover:bg-brass hover:text-ink hover:-translate-y-0.5"
            >
              Falar no WhatsApp
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink/60">
              Resposta em horário comercial
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: MASK_EASE, delay: 0.7 }}
          className="relative hidden lg:block"
        >
          <div className="absolute -inset-3 border border-brass/50 translate-x-4 translate-y-4" />
          <motion.div style={{ y: yImg, rotate: rotateImg }} className="relative overflow-hidden border border-ink/15">
            <img
              src={HERO_IMAGE}
              alt="Ilustração de passaporte aberto com carimbos de viagem e avião de papel"
              data-testid="hero-image"
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-paper/85 backdrop-blur-sm border-t border-ink/10 px-5 py-3 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink/60">
                Doc. 2026 — Situação: regular
              </span>
              <Stamp className="w-4 h-4 text-carimbo" />
            </div>
          </motion.div>
          <motion.div
            initial={{ scale: 3, opacity: 0, rotate: -20 }}
            animate={{ scale: 1, opacity: 1, rotate: -9 }}
            transition={{ type: "spring", stiffness: 200, damping: 13, delay: 1.5 }}
            className="absolute -top-7 -left-9 w-[104px] h-[104px] rounded-full stamp-ring bg-paper/40 flex flex-col items-center justify-center text-carimbo"
            data-testid="hero-stamp"
          >
            <span className="font-mono text-[8.5px] tracking-[0.16em] uppercase">Suporte para</span>
            <span className="font-serif italic font-bold text-[15px] leading-none mt-1">Regularização</span>
          </motion.div>
        </motion.div>
      </div>

      <div className="border-y border-ink/10 py-4 relative z-10" data-testid="mrz-marquee">
        <Marquee speed={28} gradient={false} pauseOnHover={false}>
          {[...mrzLines, ...mrzLines].map((line, i) => (
            <span
              key={i}
              className="font-mono text-[12.5px] tracking-[0.14em] text-ink/35 mx-6 whitespace-nowrap"
            >
              {line}
            </span>
          ))}
        </Marquee>
      </div>
    </header>
  );
}
